import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@lims/permissions';
import { ALL_NUMBERING_RESET_POLICIES } from '@lims/shared-types';
import { numberingApi } from '../../api/endpoints.js';
import type { NumberingRule } from '../../api/endpoints.js';
import { ApiError } from '../../api/client.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  Dialog,
  ErrorState,
  Form,
  FormSection,
  LoadingState,
  Panel,
  SelectField,
  TextField,
  useToast,
} from '../../components/ui/index.js';

/**
 * How this laboratory numbers its records.
 *
 * EVERY VALUE HERE BELONGS TO THE LABORATORY. The prefixes, the padding, the
 * reset month: none of them is a literal anywhere in the product, and the
 * defaults the backend applies to a NEW laboratory are read from the server
 * rather than restated here, so the two cannot drift (CLAUDE.md section 6.1).
 *
 * THE PREVIEW IS LABELLED A PREVIEW. It allocates nothing and reserves
 * nothing, and the numbers shown are illustrations. Displaying a number as
 * though it had been assigned, when it has not, would be exactly the kind of
 * dishonest display the product forbids (CLAUDE.md section 8).
 *
 * ONE RULE IS REFUSED, ALWAYS: a yearly restart with no year in the format.
 * That combination would re-issue last year's numbers, and numbers are never
 * reused. The server refuses it, the shared validator refuses it, and the
 * database refuses it.
 */

/** Shown in the format hint. Written once, not restated per language. */
const TOKEN_EXAMPLES = {
  sequence: '{00000}',
  year: '{YYYY}',
  month: '{MM}',
} as const;

export function NumberingSettings() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const { show } = useToast();

  const canManage = can(PERMISSIONS.NUMBERING_MANAGE);

  const [editing, setEditing] = useState<NumberingRule | null>(null);
  const [format, setFormat] = useState('');
  const [resetPolicy, setResetPolicy] = useState<string | null>(null);
  const [preview, setPreview] = useState<string[]>([]);

  const rules = useQuery({
    queryKey: ['numbering'],
    queryFn: numberingApi.list,
  });

  /**
   * Preview whatever is currently typed.
   *
   * Debounced, so a half-typed format does not produce a stream of complaints
   * while somebody is still writing it.
   */
  useEffect(() => {
    if (!editing || !format.trim() || !resetPolicy) {
      setPreview([]);
      return;
    }
    const handle = setTimeout(() => {
      numberingApi
        .preview({ format: format.trim(), resetPolicy })
        .then((result) => setPreview(result.preview))
        // A rejected preview is not an error worth shouting about: the format
        // is simply not finished yet. The save path reports the real problem.
        .catch(() => setPreview([]));
    }, 400);
    return () => clearTimeout(handle);
  }, [editing, format, resetPolicy]);

  const save = useMutation({
    mutationFn: () =>
      numberingApi.update(editing?.recordType as string, {
        format: format.trim(),
        resetPolicy: resetPolicy as string,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['numbering'] });
      setEditing(null);
      show({ tone: 'success', message: t('settings.numbering.saved') });
    },
  });

  const problems =
    save.error instanceof ApiError
      ? (save.error.details ?? [])
          .map((detail) => detail.code)
          .filter((code): code is string => Boolean(code))
      : [];

  const openEdit = (rule: NumberingRule) => {
    setEditing(rule);
    setFormat(rule.format);
    setResetPolicy(rule.resetPolicy);
    setPreview(rule.preview);
    save.reset();
  };

  if (rules.isLoading) return <LoadingState label={t('common.loading')} />;
  if (rules.error) {
    return <ErrorState error={rules.error} onRetry={() => void rules.refetch()} />;
  }

  return (
    <Panel
      title={t('settings.numbering.title')}
      description={t('settings.numbering.description')}
    >
      <ul className="flex flex-col gap-stack">
        {(rules.data?.rules ?? []).map((rule) => (
          <li
            key={rule.recordType}
            className="rounded-medium border border-default-200 p-4"
          >
            <div className="flex flex-wrap items-start gap-inline">
              <div className="min-w-0 flex-1">
                <p className="text-body font-medium text-default-900">
                  {t(`settings.numbering.record.${rule.recordType}`)}
                </p>
                {/* The format is a machine pattern, always left-to-right. */}
                <p className="text-caption text-default-600" dir="ltr">
                  {rule.format}
                </p>
                <p className="text-caption text-default-600">
                  {t(`settings.numbering.reset.${rule.resetPolicy}`)}
                </p>
              </div>

              <div className="min-w-0">
                <p className="text-caption text-default-600">
                  {t('settings.numbering.preview')}
                </p>
                <ul className="text-caption text-default-700" dir="ltr">
                  {rule.preview.map((example) => (
                    <li key={example}>{example}</li>
                  ))}
                </ul>
              </div>

              {canManage ? (
                <ActionButton
                  priority="secondary"
                  size="sm"
                  onPress={() => openEdit(rule)}
                >
                  {t('common.edit')}
                </ActionButton>
              ) : null}
            </div>
          </li>
        ))}
      </ul>

      <p className="mt-stack text-caption text-default-600">
        {t('settings.numbering.previewHint')}
      </p>

      <Dialog
        isOpen={editing !== null}
        onClose={() => setEditing(null)}
        title={
          editing
            ? t(`settings.numbering.record.${editing.recordType}`)
            : t('settings.numbering.title')
        }
      >
        {problems.map((problem) => (
          <div key={problem} className="mb-stack">
            <Alert
              tone="warning"
              title={t(`settings.numbering.error.${problem}`, {
                sequence: TOKEN_EXAMPLES.sequence,
              })}
            />
          </div>
        ))}

        <Form
          onSubmit={() => save.mutate()}
          isSubmitting={save.isPending}
          actions={
            <ActionBar>
              <ActionButton priority="tertiary" onPress={() => setEditing(null)}>
                {t('common.cancel')}
              </ActionButton>
              <ActionButton
                priority="primary"
                type="submit"
                isLoading={save.isPending}
              >
                {t('common.save')}
              </ActionButton>
            </ActionBar>
          }
        >
          <FormSection title={t('settings.numbering.format')}>
            <div dir="ltr">
              <TextField
                label={t('settings.numbering.format')}
                description={t('settings.numbering.formatHint', TOKEN_EXAMPLES)}
                value={format}
                onChange={setFormat}
                isRequired
              />
            </div>

            <SelectField
              label={t('settings.numbering.resetPolicy')}
              value={resetPolicy}
              onChange={setResetPolicy}
              options={ALL_NUMBERING_RESET_POLICIES.map((policy) => ({
                value: policy,
                label: t(`settings.numbering.reset.${policy}`),
              }))}
            />

            <div>
              <p className="text-caption text-default-600">
                {t('settings.numbering.preview')}
              </p>
              <ul className="text-body text-default-800" dir="ltr">
                {preview.map((example) => (
                  <li key={example}>{example}</li>
                ))}
              </ul>
              <p className="text-caption text-default-600">
                {t('settings.numbering.previewHint')}
              </p>
            </div>

            {editing ? (
              <p className="text-caption text-default-600">
                {t('settings.numbering.issuedThisPeriod')}:{' '}
                {editing.issuedThisPeriod}
              </p>
            ) : null}
          </FormSection>
        </Form>
      </Dialog>
    </Panel>
  );
}
